import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { Loader, CheckCircle, XCircle, Clock } from "lucide-react";

export const StatusTab = ({
    jobs,
    streams,
    loading,
    syncing
}) => {
    // Stats for charts
    const chartData = React.useMemo(() => {
        if (!jobs.length) return [];

        // Group jobs by date
        const stats = {};
        const now = new Date();
        // Initialize last 30 days
        for (let i = 29; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            stats[dateStr] = { date: dateStr, count: 0, records: 0, failed: 0 };
        }

        jobs.forEach(job => {
            const date = new Date(job.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            if (stats[date]) {
                stats[date].count++;
                if (job.status === 'failed') stats[date].failed++;
                if (job.rowsSynced) stats[date].records += job.rowsSynced;
            }
        });

        return Object.values(stats);
    }, [jobs]);

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center">
                <Loader className="w-6 h-6 text-primary-600 animate-spin mr-3" />
                <span className="text-gray-500">Loading connection data...</span>
            </div>
        );
    }

    const enabledStreams = streams.filter((s) => s.enabled || s.selected);
    const latestJob = jobs[0];
    const isSyncing = latestJob?.status === "running" || syncing;

    // Helper to format relative time
    const formatRelativeTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffSecs < 60) return "a few seconds ago";
        if (diffMins < 60)
            return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
        if (diffHours < 24)
            return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    };

    return (
        <div className="p-6 space-y-6">
            {/* Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Streams Status Chart */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Streams status</h3>
                    <div className="flex items-center space-x-4 mb-4 text-xs text-gray-500">
                        <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span> {jobs.length} syncs</span>
                        <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span> {jobs.filter(j => j.status === 'failed').length} failed</span>
                    </div>
                    <div className="h-40 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                />
                                <Bar dataKey="count" fill="#E5E7EB" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="failed" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Records Loaded Chart */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Records loaded</h3>
                    <div className="flex items-center space-x-4 mb-4 text-xs text-gray-500">
                        <span>Total: {jobs.reduce((acc, job) => acc + (job.rowsSynced || 0), 0).toLocaleString()} records</span>
                    </div>
                    <div className="h-40 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                <Tooltip
                                    formatter={(value) => [value.toLocaleString(), "Records"]}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                />
                                <Bar dataKey="records" fill="#8B5CF6" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Active Streams Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                    <span className="col-span-2">Status</span>
                    <span className="col-span-4">Stream name</span>
                    <span className="col-span-3">
                        Latest sync in{" "}
                        <select className="ml-1 text-xs bg-gray-50 border border-gray-300 rounded px-1 py-0.5 normal-case">
                            <option>records</option>
                            <option>bytes</option>
                        </select>
                    </span>
                    <span className="col-span-2">Data fresh as of</span>
                    <span className="col-span-1"></span>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-100">
                    {enabledStreams.map((stream) => (
                        <div
                            key={stream.name}
                            className="grid grid-cols-12 px-6 py-4 text-sm items-center hover:bg-gray-50"
                        >
                            {/* Status */}
                            <span className="col-span-2 flex items-center">
                                {isSyncing ? (
                                    <>
                                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                                        <span className="text-gray-700">Syncing</span>
                                    </>
                                ) : latestJob?.status === "succeeded" ? (
                                    <>
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        <span className="text-gray-700">Synced</span>
                                    </>
                                ) : latestJob?.status === "failed" ? (
                                    <>
                                        <XCircle className="w-4 h-4 text-red-500 mr-2" />
                                        <span className="text-gray-700">Failed</span>
                                    </>
                                ) : (
                                    <>
                                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-gray-700">Pending</span>
                                    </>
                                )}
                            </span>

                            {/* Stream name */}
                            <span className="col-span-4 text-gray-900 font-medium">
                                {stream.name}
                            </span>

                            {/* Latest sync in */}
                            <span className="col-span-3 text-gray-500">
                                {isSyncing
                                    ? "Starting..."
                                    : latestJob?.rowsSynced
                                        ? `${latestJob.rowsSynced.toLocaleString()} records`
                                        : "-"}
                            </span>

                            {/* Data fresh as of */}
                            <span className="col-span-2 text-gray-500">
                                {formatRelativeTime(latestJob?.completedAt)}
                            </span>

                            {/* Action menu */}
                            <span className="col-span-1 flex justify-end">
                                <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                                    <svg
                                        className="w-5 h-5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                    </svg>
                                </button>
                            </span>
                        </div>
                    ))}

                    {enabledStreams.length === 0 && (
                        <div className="px-6 py-12 text-center text-gray-500">
                            No streams enabled. Go to Schema tab to enable streams.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
